import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import {
  RecentActivityItem,
  SuperAdminDashboardService,
} from 'src/app/core/services/super-admin-dashboard.service';
import {
  ActivitiesTableComponent,
  ActivityRow,
} from './activities-table/activities-table.component';

const RESULT_MAP: Record<string, string> = {
  Approved: 'PASS',
  'Under Review': 'REVIEW',
  Rejected: 'FAIL',
};

@Component({
  selector: 'app-activities',
  standalone: true,
  imports: [
    CommonModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ActivitiesTableComponent,
  ],
  templateUrl: './activities.component.html',
})
export class ActivitiesComponent implements OnInit {
  private readonly dashboardService = inject(SuperAdminDashboardService);
  private readonly router = inject(Router);

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search customer or agent…' },
      status: {
        options: [
          { value: '', label: 'All' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Under Review', label: 'Under Review' },
          { value: 'Rejected', label: 'Rejected' },
        ],
      },
      dateRange: false,
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };

  rows: ActivityRow[] = [];
  loading = false;
  listError = '';
  page = 1;
  readonly pageSize = 20;
  meta: ListModulePaginationMeta | null = null;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.listError = '';
    const offset = (this.page - 1) * this.pageSize;
    this.dashboardService
      .getActivities({
        search: this.listFilters.searchQuery.trim() || undefined,
        result: RESULT_MAP[this.listFilters.status] || undefined,
        page: this.page,
        pageSize: this.pageSize,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          const data = res?.data;
          const items = data?.data ?? [];
          this.rows = items.map((a, i) => this.mapToActivityRow(a, offset + i + 1));
          this.setMetaFromTotal(data?.total ?? 0);
        },
        error: () => {
          this.rows = [];
          this.meta = null;
          this.listError = 'Could not load activities.';
        },
      });
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.load();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
    this.load();
  }

  onView(row: ActivityRow): void {
    if (!row.sessionId) return;
    const queryParams: Record<string, string> = {
      session: String(row.sessionId),
      return: '/activities',
    };
    if (row.sessionSource === 'tenant' && row.tenantId) {
      queryParams['tenant_id'] = String(row.tenantId);
    }
    void this.router.navigate(['/audit-trail'], { queryParams });
  }

  private setMetaFromTotal(total: number): void {
    const lastPage = Math.max(1, Math.ceil(total / this.pageSize));
    this.meta = { total, lastPage, currentPage: this.page, perPage: this.pageSize };
  }

  private mapToActivityRow(a: RecentActivityItem, srNo: number): ActivityRow {
    const status = this.statusLabel(a);
    return {
      srNo,
      sessionId: a.id,
      sessionCode: a.session_code ?? null,
      sessionSource: a.session_source ?? 'platform',
      customerName: a.customer_name?.trim() || 'Unknown',
      agentName: a.agent_name?.trim() || 'Unknown Agent',
      description: this.describe(a, status),
      status,
      tenantId: a.tenant_id,
      created_at: a.created_at,
    };
  }

  private statusLabel(a: RecentActivityItem): string {
    const r = (a.result ?? '').trim().toUpperCase();
    if (r === 'PASS') return 'Approved';
    if (r === 'REVIEW') return 'Under Review';
    if (r === 'FAIL') return 'Rejected';
    const st = (a.status ?? '').trim().toUpperCase();
    const lifecycle: Record<string, string> = {
      COMPLETED: 'Completed',
      KYC_DONE: 'KYC Done',
      INTERVIEW_DONE: 'Interview Done',
      IN_PROGRESS: 'In progress',
      PENDING: 'Pending',
    };
    return lifecycle[st] ?? (a.status?.trim() || '—');
  }

  private describe(a: RecentActivityItem, status: string): string {
    const customer = a.customer_name?.trim() || 'Customer';
    const agent = a.agent_name?.trim() || 'an agent';
    if (status === 'Approved') return `${customer} completed PD via ${agent} — Approved.`;
    if (status === 'Rejected') return `${customer}'s PD via ${agent} was Rejected.`;
    if (status === 'Under Review') return `${customer}'s PD via ${agent} is Under Review.`;
    return `${customer} completed the PD session via ${agent}.`;
  }
}
